import { useState } from 'react';
import { BottomSheet } from '@/components/base/BottomSheet';
import { MobileMetricCard } from '@/components/base/MobileUi';
import { StatusBadge } from '@/components/base/StatusBadge';
import { getFarmerKyc } from '@/store';
import type { AdvanceRequest, Contract, Debt, Farmer } from '@/types';
import { formatCurrencyCompact } from '@/utils/formatters';

interface LoanDetailModalProps {
  loan: AdvanceRequest;
  farmer: Farmer;
  farmerName: string;
  seasonName: string;
  fieldName: string;
  debts: Debt[];
  contracts: Contract[];
  onClose: () => void;
  onNeedMore: () => void;
  onViewContract: (contractId: string) => void;
}

/** Approve/reject and exception-approval are now Agrida-internal decisions (CO →
 * DEPT_HEAD → DIRECTOR via the Admin Portal BFF, not this app — see task 27 notes).
 * HTX_STAFF can only view status and request more info from the farmer. */
export default function LoanDetailModal({
  loan,
  farmer,
  farmerName,
  seasonName,
  fieldName,
  debts,
  contracts,
  onClose,
  onNeedMore,
  onViewContract,
}: LoanDetailModalProps) {
  const [showHistory, setShowHistory] = useState(false);
  const kyc = getFarmerKyc(farmer);
  const activeContracts = contracts.filter(
    (contract) => contract.farmerId === loan.farmerId && contract.status === 'Đang hiệu lực',
  );
  const activeDebts = debts.filter((debt) => debt.status !== 'Đã tất toán');
  const totalDebt = activeDebts.reduce((sum, debt) => sum + debt.conNo, 0);
  const overdueDebts = debts.filter((debt) => debt.status === 'Quá hạn' || debt.status === 'Thu nợ lỗi');

  const canRequestMoreInfo = loan.status === 'Chờ kiểm tra';

  const footer = canRequestMoreInfo ? (
    <div className="mobile-action-row">
      <button type="button" onClick={onClose} className="rounded-xl bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700">
        Đóng
      </button>
      <button type="button" onClick={onNeedMore} className="rounded-xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700">
        Yêu cầu bổ sung
      </button>
    </div>
  ) : (
    <button type="button" onClick={onClose} className="w-full rounded-xl bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700">
      Đóng
    </button>
  );

  return (
    <BottomSheet open onClose={onClose} title="Chi tiết khoản vay" footer={footer}>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-slate-900">{farmerName}</p>
            <p className="metric-caption mt-0.5 truncate text-[10px] text-slate-400">
              {loan.id.toUpperCase()} · {seasonName || 'Chưa gắn mùa vụ'}
            </p>
          </div>
          <StatusBadge status={loan.status} />
        </div>

        <section className="rounded-2xl border border-agrida-200 bg-agrida-50 p-4 text-center">
          <p className="text-[11px] font-medium text-agrida-700">Số tiền đề nghị</p>
          <strong className="mt-1 block text-2xl text-agrida-700">{formatCurrencyCompact(loan.soTien)}</strong>
          <p className="metric-caption mt-2 text-xs text-agrida-700">{loan.mucDich}</p>
        </section>

        <div className="grid grid-cols-2 gap-2">
          <MobileMetricCard
            label="eKYC"
            value={kyc.kycStatus === 'ekyc_verified' ? 'Đã xác thực' : 'Chưa đạt'}
            caption={kyc.creditScore ? `Điểm ${kyc.creditScore}` : 'Chưa có điểm'}
            tone={kyc.kycStatus === 'ekyc_verified' ? 'emerald' : 'amber'}
            icon="ri-shield-user-line"
          />
          <MobileMetricCard
            label="Công nợ"
            value={formatCurrencyCompact(totalDebt)}
            caption={overdueDebts.length ? `${overdueDebts.length} khoản quá hạn` : 'Không quá hạn'}
            tone={overdueDebts.length ? 'rose' : 'slate'}
            icon="ri-money-cny-circle-line"
          />
        </div>

        {loan.lyDoTuChoi && (
          <div className="rounded-xl bg-rose-50 px-3 py-2.5 text-xs text-rose-700">
            <strong>Lý do từ chối:</strong> {loan.lyDoTuChoi}
          </div>
        )}
        {loan.noiDungCanBoSung && (
          <div className="rounded-xl bg-amber-50 px-3 py-2.5 text-xs text-amber-700">
            <strong>Cần bổ sung:</strong> {loan.noiDungCanBoSung}
          </div>
        )}

        {(fieldName || activeContracts.length > 0) && (
          <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5 text-xs">
            <span className="min-w-0 truncate text-slate-500">{fieldName || 'Hồ sơ khoản vay'}</span>
            {activeContracts.length > 0 && (
              <button type="button" onClick={() => onViewContract(activeContracts[0].id)} className="shrink-0 font-semibold text-agrida-700">
                Xem hợp đồng
              </button>
            )}
          </div>
        )}

        {debts.length > 0 && (
          <section className="mobile-panel p-0">
            <button
              type="button"
              onClick={() => setShowHistory((value) => !value)}
              className="flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left"
            >
              <span className="text-sm font-bold text-slate-900">Trạng thái công nợ</span>
              <i className={`ri-arrow-down-s-line text-lg text-slate-400 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
            </button>
            {showHistory && (
              <div className="space-y-2 border-t border-slate-100 p-3">
                {debts.map((debt) => (
                  <div key={debt.id} className="rounded-xl bg-slate-50 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-slate-700">{debt.status}</span>
                      <span className="text-[10px] text-slate-400">{debt.ngayDenHan}</span>
                    </div>
                    <p className="metric-caption mt-1 text-[10px] text-slate-500">Còn nợ: {formatCurrencyCompact(debt.conNo)}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </BottomSheet>
  );
}
